import {ChartPlotStyle, InsightsBookmarkParams, InsightsResourceType} from 'common/report/insights/util/types';
import {ChartType, PRIMITIVE_MATH_TYPES} from 'common/report/constants';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {LiftType, LiftTypeOverrides} from 'common/report/util/lift/types';
import {getLiftTypeOverrides} from 'common/report/util/lift/overrides';
import {disableLiftTypeForChart} from 'common/report/util/lift';
import {getBehaviorShowClauses, isRetentionOnlyInsightsBookmark} from 'common/report/insights/models/utils';

export function getLiftTypeOverridesForInsightsBookmark(bookmarkParams: InsightsBookmarkParams): LiftTypeOverrides {
  const {
    displayOptions: {chartType, plotStyle},
    sections: {show: showClauses, group},
  } = bookmarkParams;

  const behaviorShowClauses = getBehaviorShowClauses(showClauses);
  const hasDistributions = behaviorShowClauses.some(
    (clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM,
  );
  const isProfileQuery = behaviorShowClauses.some(
    (clause) => clause.behavior.resourceType === InsightsResourceType.People,
  );

  const hasBreakdowns = getSelectedSegments(group.map((group) => new GroupClause(group))).length > 0;

  const multipleMetricsAllTotals =
    behaviorShowClauses.length > 1 &&
    behaviorShowClauses.every((clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.TOTAL);

  const overrides = getLiftTypeOverrides(chartType, {
    isStacked: plotStyle === ChartPlotStyle.Stacked,
  });

  if (hasBreakdowns && hasDistributions) {
    overrides[LiftType.Segment] = {
      enabled: false,
      reason: `Cannot compare baselines when both distributions and segmentations are selected`,
    };
  } else if (multipleMetricsAllTotals) {
    // explicitly ENABLE compare to overall if >1 metric AND they are all only total count metrics
    overrides[LiftType.Overall] = {
      enabled: true,
    };
  }

  if (isProfileQuery && chartType === ChartType.Line) {
    overrides[LiftType.Segment] = disableLiftTypeForChart(LiftType.Segment, chartType, `Profile Line Chart`);
    overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, chartType, `Profile Line Chart`);
  }

  if (isRetentionOnlyInsightsBookmark(bookmarkParams)) {
    switch (chartType) {
      case ChartType.Line:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, ChartType.RetentionLine);
        break;
      case ChartType.Metric:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, ChartType.RetentionTrendMetric);
        break;
      case ChartType.RetentionCurve:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, ChartType.RetentionCurve);
        break;
    }
  }

  return overrides;
}
